/**
 * Traduz o motivo de uma NFS-e recusada para "o que fazer" e, quando dá,
 * para onde ir: um campo da tela de emissão ou uma aba do Painel do contador.
 *
 * A mensagem crua NUNCA é escondida — ela continua sendo a fonte de verdade
 * para o contador. Isto aqui é só um atalho.
 *
 * As regras abaixo casam com mensagens que o próprio sistema gera
 * (validações do `nf-server` em `NfseService` e do `nf-web` em `onSubmit`),
 * por isso o texto é previsível. Rejeição de prefeitura/SEFAZ varia por
 * município: só entra nesta tabela mensagem real observada em produção ou
 * sandbox — sem padrão confirmado, cai no fallback e o usuário lê o original.
 */

export type PainelCorrecao = "cadastro" | "nfe" | "nfse";

export interface RejeicaoInterpretada {
  /** O que houve, em uma linha. */
  titulo: string;
  /** O que o usuário precisa fazer. */
  comoCorrigir: string;
  /** Texto do botão de atalho, quando existe para onde levar. */
  acaoRotulo?: string;
  /** Id do bloco na tela de emissão (ver IDs `rej-*` no nfse.component.html). */
  ancora?: string;
  /** Aba do Painel do contador, quando a correção é de cadastro. */
  painel?: PainelCorrecao;
  /** Mensagem original, como veio do PlugNotas/prefeitura. */
  original: string;
  /**
   * Mensagem sem o prefixo do lote, para exibir na tela. A prefeitura devolve
   * no formato "Lote de Notas Rejeitado Pelo Servidor: E35-<descrição>".
   */
  descricao: string;
  /** Código da prefeitura, quando a mensagem traz um (ex.: "E35"). */
  codigoPrefeitura?: string;
}

/** "Lote de Notas Rejeitado Pelo Servidor: " na frente da mensagem real. */
const PREFIXO_LOTE = /^lote\s+de\s+notas\s+rejeitado\s+pelo\s+servidor:\s*/i;
/** "<codigo>-<descrição>" logo depois do prefixo. */
const CODIGO_E_DESCRICAO = /^([A-Za-z]{0,2}\d{1,4}[A-Za-z]?)\s*-\s*([\s\S]+)$/;

/**
 * Separa o código da prefeitura da descrição. Códigos só de zero ("0-...")
 * não dizem nada ao usuário e são descartados.
 */
function separarCodigo(mensagem: string): {
  descricao: string;
  codigoPrefeitura?: string;
} {
  const semPrefixo = mensagem.replace(PREFIXO_LOTE, "").trim();
  const partes = CODIGO_E_DESCRICAO.exec(semPrefixo);

  if (!partes) {
    return { descricao: semPrefixo || mensagem };
  }

  const codigo = partes[1];
  const descricao = partes[2].trim();

  return {
    descricao: descricao || semPrefixo,
    codigoPrefeitura: /^0+$/.test(codigo) ? undefined : codigo
  };
}

interface RegraRejeicao {
  teste: RegExp;
  titulo: string;
  comoCorrigir: string;
  acaoRotulo?: string;
  ancora?: string;
  painel?: PainelCorrecao;
}

/** Ordem importa: a primeira regra que casar vence, da mais específica. */
const REGRAS: RegraRejeicao[] = [
  // Fortaleza concatena rejeições na mesma mensagem ("E550-...: E630-..."),
  // então a regra mais específica (obra) vem antes da genérica (CNAE x item).
  {
    // Rejeição real de Fortaleza (16/10/2026): "E630-Se o código de tributação
    // do município pertencer a um dos subitens 07.02, 07.04, 07.05, 07.06,
    // 07.07, 07.08, 07.17 e 07.19 ... o grupo de informações de construção
    // civil/obra é obrigatório." Também cobre a pré-validação do nf-server
    // ("exige o grupo de obra").
    teste: /constru[çc][ãa]o\s+civil|grupo\s+de\s+obra|(^|\W)E630(\W|$)/i,
    titulo: "Serviço de construção civil sem os dados da obra",
    comoCorrigir:
      "Para os itens 07.02, 07.04, 07.05, 07.06, 07.07, 07.08, 07.17 e 07.19 da lista de serviços a prefeitura exige o grupo de obra (código da obra e ART; CNO, IPTU e endereço da obra quando houver). Informe-os na etapa Obra da emissão. Se o serviço não é de obra, o contador deve indicar o item da lista vinculado ao CNAE fora do grupo 07.",
    acaoRotulo: "Ir para o campo",
    ancora: "rej-obra"
  },
  {
    // Rejeição real de Fortaleza (16/10/2026): "E550-O item da lista indicado
    // não está vinculado à CNAE informada. (Informe o item da lista vinculado
    // à CNAE.)" — e a pré-validação equivalente do nf-server
    // (assertFortalezaServiceRules: "7 primeiros precisam ser iguais ao CNAE").
    teste:
      /item\s+da\s+lista\s+(indicado\s+)?n[ãa]o\s+est[áa]\s+vinculad|(^|\W)E550(\W|$)|iguais\s+ao\s+CNAE/i,
    titulo: "Item da lista não combina com o CNAE",
    comoCorrigir:
      "A prefeitura só aceita o item da lista de serviços que ela vincula ao CNAE do serviço. Em Fortaleza, consulte a trinca CNAE / item da lista / código de tributação na consulta pública de atividades da Sefin e ajuste o cadastro do serviço (CNAE, item e sub-item, código de tributação do município) em Painel do contador > NFS-e. Qual trinca usar é decisão do contador.",
    acaoRotulo: "Abrir cadastro do serviço",
    painel: "nfse"
  },
  {
    // Rejeições reais de Fortaleza (16/10/2026), que vêm concatenadas:
    // "E183-Tag CodigoTributacaoMunicipio obrigatória para distinção da
    // atividade no item de serviço" ou "Campo código de tributação do
    // município informado incorretamente (... obrigatório e deverá ter 9
    // caracteres)", seguidas de "E606-Campo Código NBS inexistente ou
    // incorreto (... correspondente ao Código de Tributação do Município)" e
    // "E637-O código de tributação do município deve corresponder ao NBS,
    // código indicador da operação, CST e CClassTrib". A causa é uma só: o
    // código de tributação não foi enviado (ou não bate com a tabela), e a
    // NBS/CST/cClassTrib são validados contra ele. Também cobre a
    // pré-validação do nf-server ("código de tributação do município com 9
    // dígitos").
    teste:
      /c[óo]digo\s+de\s+tributa[çc][ãa]o\s+do\s+munic[íi]pio|CodigoTributacaoMunicipio|(^|\W)E(183|606|637)(\W|$)/i,
    titulo: "Código de tributação do município ausente ou incompatível",
    comoCorrigir:
      "Em Fortaleza o código de tributação do município é obrigatório, tem 9 dígitos (o CNAE de 7 dígitos seguido do desdobramento de 2 dígitos) e precisa corresponder ao item da lista, à NBS e à classificação IBS/CBS (CST e cClassTrib) do serviço. Preencha-o no cadastro do serviço, em Painel do contador > NFS-e, com o código que a Sefin publica para o CNAE.",
    acaoRotulo: "Abrir cadastro do serviço",
    painel: "nfse"
  },
  {
    // Rejeição real da prefeitura (relatório de erro de 13/08/2026):
    // "Lote de Notas Rejeitado Pelo Servidor: E35-Código de tributação
    // inexistente. (Consulte a legislação vigente...)"
    teste: /c[óo]digo\s+de\s+tributa[çc][ãa]o\s+inexistente|(^|\W)E35(\W|$)/i,
    titulo: "A prefeitura não reconheceu o código de tributação",
    comoCorrigir:
      "O código de tributação enviado não existe na lista do município. Confirme com o contador qual código vale para este serviço e ajuste o cadastro (Código do serviço / Código do sub-item), em Painel do contador > NFS-e.",
    acaoRotulo: "Abrir cadastro do serviço",
    painel: "nfse"
  },
  {
    // Rejeição real da prefeitura (relatório de erro de 14/08/2026):
    // "Lote de Notas Rejeitado Pelo Servidor: 0-Alíquota deve ser entre 2% e 5% (0)"
    teste: /al[íi]quota\s+deve\s+ser|al[íi]quota\s+(inv[áa]lida|fora)/i,
    titulo: "Alíquota do ISS fora da faixa aceita",
    comoCorrigir:
      "A prefeitura só aceita a alíquota dentro da faixa que ela informa na mensagem abaixo — foi enviada uma alíquota fora dela (normalmente zero, quando o serviço ficou sem alíquota no cadastro). Corrija no serviço, em Painel do contador > NFS-e; o valor correto é decisão do contador.",
    acaoRotulo: "Abrir cadastro do serviço",
    painel: "nfse"
  },
  {
    // nf-server: applyRegularNfseIbscbsDefaults
    teste:
      /c[óo]digo\s+NBS\s+do\s+servi[çc]o\s+\d+\s+[ée]\s+obrigat[óo]rio|(^|\W)E351(\W|$)|NBS\s+n[ãa]o\s+informad/i,
    titulo: "Falta o código NBS do serviço",
    comoCorrigir:
      "O código NBS é obrigatório na emissão com IBS/CBS. Cadastre-o no serviço, em Painel do contador > NFS-e, e emita de novo.",
    acaoRotulo: "Abrir cadastro do serviço",
    painel: "nfse"
  },
  {
    // nf-server: applyRegularNfseIbscbsDefaults (CCT/CST, código da operação,
    // operação de uso pessoal)
    teste:
      /(classifica[çc][ãa]o\s+CCT|situa[çc][ãa]o\s+CST|IBS\/CBS|opera[çc][ãa]o\s+de\s+uso\s+pessoal)/i,
    titulo: "Falta a classificação IBS/CBS do serviço",
    comoCorrigir:
      "Código da operação, operação de uso pessoal, CCT e CST ficam no cadastro do serviço (Painel do contador > NFS-e). Quais valores usar é decisão do contador.",
    acaoRotulo: "Abrir cadastro do serviço",
    painel: "nfse"
  },
  {
    // nf-server: assertServicesParametrized
    teste: /est[áa]\s+inativo/i,
    titulo: "O serviço está inativo",
    comoCorrigir:
      "Serviço inativo não entra na emissão. Ative-o em Painel do contador > NFS-e — ativar exige código, discriminação e alíquota do ISS preenchidos.",
    acaoRotulo: "Abrir cadastro do serviço",
    painel: "nfse"
  },
  {
    // nf-server: assertServicesParametrized (alíquota ausente)
    teste: /n[ãa]o\s+foi\s+parametrizado|al[íi]quota\s+do\s+ISS/i,
    titulo: "O cadastro do serviço está incompleto",
    comoCorrigir:
      "Complete o serviço em Painel do contador > NFS-e (alíquota do ISS e o que o aviso apontar) antes de emitir.",
    acaoRotulo: "Abrir cadastro do serviço",
    painel: "nfse"
  },
  {
    // nf-server: normalizeTaxReformServiceCodes
    teste:
      /c[óo]digo\s+nacional\s+do\s+servi[çc]o|exatamente\s+6\s+d[íi]gitos/i,
    titulo: "Código do serviço fora do padrão nacional",
    comoCorrigir:
      "O código precisa ter os 6 dígitos da tabela nacional (ex.: 070602). Ajuste no cadastro do serviço, em Painel do contador > NFS-e.",
    acaoRotulo: "Abrir cadastro do serviço",
    painel: "nfse"
  },
  {
    // nf-server: issue (série) — a série vem das Configurações quando a
    // numeração é automática, mas é escolhida na emissão.
    teste: /s[ée]rie\s+do\s+RPS/i,
    titulo: "Problema na série do RPS",
    comoCorrigir:
      "Confira a série do RPS na emissão (no máximo 5 caracteres). Com numeração automática ela vem das Configurações da empresa em Emissão de NFS-e.",
    acaoRotulo: "Ir para a série do RPS",
    ancora: "rej-rps"
  },
  {
    // nf-web: onSubmit (limites de tamanho do tomador)
    teste: /raz[ãa]o\s+social\s+do\s+tomador|logradouro\s+do\s+tomador/i,
    titulo: "Dados do tomador fora do limite aceito",
    comoCorrigir:
      "A razão social ou o logradouro do tomador passou do limite de caracteres indicado na mensagem (115 e 60, respectivamente, na NFS-e). Encurte o texto nos dados do destinatário.",
    acaoRotulo: "Ir para o destinatário",
    ancora: "rej-tomador"
  },
  {
    // nf-web: onSubmit (IBGE do tomador) e validações de endereço
    teste: /endere[çc]o\s+do\s+tomador|c[óo]digo\s+de\s+cidade\s+IBGE/i,
    titulo: "Endereço do tomador incompleto",
    comoCorrigir:
      "Cidade e estado do tomador precisam estar preenchidos, com código IBGE de 7 dígitos. Refaça a busca pelo CEP se o código não tiver vindo.",
    acaoRotulo: "Ir para o destinatário",
    ancora: "rej-tomador"
  },
  {
    // nf-web: onSubmit (tomador vazio)
    teste: /dados\s+do\s+tomador/i,
    titulo: "Faltam dados do tomador",
    comoCorrigir:
      "Informe CPF/CNPJ e razão social do destinatário antes de emitir.",
    acaoRotulo: "Ir para o destinatário",
    ancora: "rej-tomador"
  },
  {
    // nf-web: onSubmit (IBGE da prestação)
    teste: /cidade\s+de\s+presta[çc][ãa]o/i,
    titulo: "Cidade de prestação inválida",
    comoCorrigir:
      "A cidade de prestação precisa ser informada com código IBGE válido. Com ISS devido no município, ela deve ser a cidade do emitente.",
    acaoRotulo: "Ir para a cidade de prestação",
    ancora: "rej-cidade-prestacao"
  },
  {
    // nf-web: onSubmit (valor por item)
    teste: /valor\s+de\s+cada\s+servi[çc]o|valor\s+do\s+servi[çc]o/i,
    titulo: "Valor do serviço não informado",
    comoCorrigir:
      "Cada serviço da nota precisa de um valor maior que zero, informado na emissão.",
    acaoRotulo: "Ir para os serviços",
    ancora: "rej-servicos"
  },
  {
    // nf-server: issue (limite do plano)
    teste: /limite\s+de\s+notas/i,
    titulo: "Limite de notas do mês atingido",
    comoCorrigir:
      "A empresa chegou ao limite de emissões do mês. Fale com o suporte para ampliar o limite.",
    painel: "cadastro"
  },
  {
    // nf-web: getCertificateValidationMessage / pendências de certificado
    teste: /certificado/i,
    titulo: "Problema no certificado digital",
    comoCorrigir:
      "Sem certificado válido a prefeitura não aceita a nota. Confira a validade e reenvie o arquivo em Painel do contador > Meu CNPJ.",
    acaoRotulo: "Abrir Meu CNPJ",
    painel: "cadastro"
  },
  {
    // pendências: IM obrigatória quando NFS-e está ativa
    teste: /inscri[çc][ãa]o\s+municipal/i,
    titulo: "Inscrição municipal ausente ou inválida",
    comoCorrigir:
      "A prefeitura exige a inscrição municipal do prestador. Corrija em Painel do contador > Meu CNPJ e salve o cadastro.",
    acaoRotulo: "Abrir Meu CNPJ",
    painel: "cadastro"
  }
];

const FALLBACK: Omit<
  RejeicaoInterpretada,
  "original" | "descricao" | "codigoPrefeitura"
> = {
  titulo: "A nota foi recusada",
  comoCorrigir:
    "Leia o motivo abaixo e ajuste o que foi apontado. Se o problema estiver no cadastro do serviço ou da empresa, corrija no Painel do contador antes de reemitir.",
  acaoRotulo: undefined
};

/**
 * @param mensagem motivo da rejeição como veio do PlugNotas/prefeitura.
 * @returns a orientação, ou `null` quando não há mensagem nenhuma.
 */
export function interpretarRejeicao(
  mensagem?: string | null
): RejeicaoInterpretada | null {
  const original = (mensagem ?? "").trim();

  if (!original) return null;

  const { descricao, codigoPrefeitura } = separarCodigo(original);
  const regra = REGRAS.find((r) => r.teste.test(original));

  if (!regra) {
    return { ...FALLBACK, original, descricao, codigoPrefeitura };
  }

  return {
    titulo: regra.titulo,
    comoCorrigir: regra.comoCorrigir,
    acaoRotulo: regra.acaoRotulo,
    ancora: regra.ancora,
    painel: regra.painel,
    original,
    descricao,
    codigoPrefeitura
  };
}

/** Há para onde levar o usuário a partir desta rejeição? */
export function temAtalhoDeCorrecao(
  rejeicao?: RejeicaoInterpretada | null
): boolean {
  return !!rejeicao && !!(rejeicao.ancora || rejeicao.painel);
}
